import { useCallback, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

import { Button } from '@unreserved-frontend-v2/ui/button/button'
import { FilterChangeEvent, FilterSelections } from '@unreserved-frontend-v2/ui/schema-table/models'
import { ToggleButtonItem } from '@unreserved-frontend-v2/ui/ui-toggle-button-group/ui-toggle-button-group'
import { DEFAULT_LOCALE } from '@unreserved-frontend-v2/utils/locale'

export interface FiltersResidentialSubType {
  label: string
  value: string
  count?: number
}

export interface FiltersResidentialTypesAndSubTypesBaseProps {
  currentSelections: FilterSelections
  residentialTypes: ToggleButtonItem[]
  residentialSubTypes: FiltersResidentialSubType[]
  onChange: (event: FilterChangeEvent | FilterChangeEvent[]) => void
}

export const useResidentialTypesAndSubTypesMethods = ({
  currentSelections,
  residentialSubTypes,
  onChange,
}: FiltersResidentialTypesAndSubTypesBaseProps) => {
  const selectedResidentialTypes = useMemo(
    () => (currentSelections?.['residentialTypes'] || []) as string[],
    [currentSelections]
  )
  const selectedResidentialSubTypes = useMemo(
    () => (currentSelections?.['residentialSubTypes'] || []) as string[],
    [currentSelections]
  )

  const onResidentialTypeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: string) => {
      onChange([
        { key: 'residentialTypes', value },
        ...(!selectedResidentialTypes.includes(value)
          ? // Clear residential sub types if filtering by a new residential type
            [
              {
                key: 'residentialSubTypes',
                value: [] as string[],
              },
            ]
          : []),
      ])
    },
    [onChange, selectedResidentialTypes]
  )

  const onResidentialSubTypeChange = useCallback(
    (value: string) => () => {
      onChange({
        key: 'residentialSubTypes',
        value: !selectedResidentialSubTypes.includes(value)
          ? selectedResidentialSubTypes.concat(value)
          : selectedResidentialSubTypes.filter((val) => val !== value),
      })
    },
    [onChange, selectedResidentialSubTypes]
  )

  // TODO: [V2-1118] Refactor into a generic multi-select filter with counts
  const subTypes = useMemo(() => {
    return selectedResidentialTypes.length && residentialSubTypes.some(({ count }) => count) ? (
      <div className="flex flex-wrap gap-2">
        {residentialSubTypes.map(({ label, value, count = 0 }) => {
          if (count === 0) {
            return null
          }

          const isSelected = selectedResidentialSubTypes.includes(value)

          return (
            <Button
              key={label}
              onClick={onResidentialSubTypeChange(value)}
              variant={isSelected ? 'selected' : 'ghostLight'}
              className={twMerge(
                'p-0',
                !isSelected
                  ? // Special override removal of ghostLight hover effects
                    'active:hover:border-shades-200 hover:border-shades-200 hover:bg-white'
                  : ''
              )}
            >
              <span className="flex items-center gap-2 pl-4 pr-2.5 text-xs">
                {label}
                <div className="bg-primary flex h-5 min-w-[25px] items-center justify-center rounded-[5px] px-1.5 text-white">
                  {count.toLocaleString(DEFAULT_LOCALE)}
                </div>
              </span>
            </Button>
          )
        })}
      </div>
    ) : null
  }, [onResidentialSubTypeChange, residentialSubTypes, selectedResidentialSubTypes, selectedResidentialTypes.length])

  return useMemo(
    () => ({
      onResidentialTypeChange,
      selectedResidentialTypes,
      subTypes,
    }),
    [onResidentialTypeChange, selectedResidentialTypes, subTypes]
  )
}
